import WorkflowsMenuItem from "@/pages/deals/components/DealStages/components/WorkflowsMenuItem";
import {
  DealQuestionnaireResults,
  DealQuestionnaireSettings,
  DealsQuestionnaireData,
  DealStage
} from "@/pages/deals/types";
import { find, map, sortBy } from "lodash";
import React, { useMemo } from "react";

interface Props {
  dealStage: number | null;
  stages: DealStage[] | undefined;
  stagesIds: number[];
  questionnairesSettings: DealQuestionnaireSettings;
  questionnairesResponses: DealQuestionnaireResults;
  selectedQuestionnaireId: string | undefined;
  onQuestionnaireSelect: (questionnaireId: string) => void;
  onStageSelect?: (stageId: number) => void;
  selectedStageId?: number | null;
  isLoadingScore: boolean;
}

const WorkflowsMenu: React.FC<Props> = ({
  dealStage,
  stages,
  stagesIds,
  questionnairesSettings,
  questionnairesResponses,
  selectedQuestionnaireId,
  onQuestionnaireSelect,
  onStageSelect,
  selectedStageId,
  isLoadingScore
}) => {
  const currentStage = useMemo(
    () => find(stages, (stage) => stage.id === dealStage),
    [stages, dealStage]
  );

  const getStageQuestionnaires = useMemo(
    () =>
      (stage: DealStage): DealsQuestionnaireData[] => {
        const questionnaires =
          questionnairesSettings[stage.id]?.data?.questionnaires;
        const activeQuestionnaires = questionnaires?.filter(
          (q) => !q.isDeleted
        );
        const sortedQuestionnaires = sortBy(
          activeQuestionnaires,
          "questionnaireOrder"
        );

        return map(sortedQuestionnaires, (questionnaire) => {
          const responses =
            questionnairesResponses[questionnaire?.id]?.data?.responses;
          const score = questionnairesResponses[questionnaire?.id]?.data?.score;
          return {
            ...questionnaire,
            responses,
            score
          };
        });
      },
    [questionnairesSettings, questionnairesResponses]
  );

  return (
    <div className="relative min-w-[320px] lg:min-w-[360px] xxl:min-w-[420px] max-h-full">
      <div
        className="max-h-full overflow-y-scroll py-2 pb-14 flex flex-col gap-4"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none"
        }}
        dir="rtl"
      >
        <div dir="ltr">
          {map(stagesIds, (stageId) => {
            const stageData = find(stages, (st) => st?.id === stageId);
            if (!stageData) return;
            const stageQuestionnaires = getStageQuestionnaires(stageData);
            const isCurrentStage = currentStage?.id === stageData.id;
            const isPreviousStage =
              currentStage && stageData.stageOrder < currentStage.stageOrder;
            const hasSelectedQuestionnaire = stageQuestionnaires.some(
              (q) => q.id === selectedQuestionnaireId
            );

            return (
              <WorkflowsMenuItem
                key={stageData.id}
                stage={stageData}
                isCurrentStage={isCurrentStage}
                isPreviousStage={Boolean(isPreviousStage)}
                hasSelectedQuestionnaire={hasSelectedQuestionnaire}
                stageQuestionnaires={stageQuestionnaires}
                selectedQuestionnaireId={selectedQuestionnaireId}
                onQuestionnaireSelect={onQuestionnaireSelect}
                onStageSelect={onStageSelect}
                selectedStageId={selectedStageId}
                isLoadingScore={isLoadingScore}
              />
            );
          })}
        </div>
      </div>
      <div
        className="absolute bottom-0 left-0 right-0 h-20 pointer-events-none"
        style={{
          background:
            "linear-gradient(0deg, var(--surface-elevation-0-page-background, #121926) 0%, rgba(18, 25, 38, 0.00) 100%)"
        }}
      />
    </div>
  );
};

export default WorkflowsMenu;
